import { CommonModule } from '@angular/common'
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    EventEmitter,
    Input,
    Output,
    QueryList
} from '@angular/core'

import { formatMinutes } from '../formatters/detail-formatters'
import {
    DetailTableColumn,
    DetailTableConfig
} from './detail-table.model'
import { DetailTableCellDirective } from './detail-table-cell.directive'
import { DetailTableExpandedDirective } from './detail-table-expanded.directive'
import {
    UiDataTableColumn,
    UiDataTableComponent,
    UiTableCellDirective,
    UiTableExpandedDirective
} from '../../ui-data-table'

/**
 * Generic typed table for detail pages.
 *
 * Driven by a `DetailTableConfig<T>` (column descriptors + optional
 * row-expander config) and an array of rows. Built-in cell formatters
 * (text/minutes/percent/integer) cover the common cases; anything more
 * elaborate (a mini-bar, a chip cluster, a clickable badge) goes
 * through the `*detailTableCell="'columnId'"` content slot.
 *
 * Expander rows are wired via `<ng-template detailTableExpanded>`. The
 * expanded content row appears immediately under the summary row when
 * the row's key is in `config.expand.expanded`. Toggling emits the row
 * via `(rowToggled)` — the parent owns the Set so its expansion state
 * survives async refreshes.
 *
 * The `data-testid` contract:
 *   - the `<table>` carries `config.testId`
 *   - each summary row carries `data-row-key="<rowKey>"`
 *   - the expander button gets `data-testid="<testId>-expander"`
 *   - each expanded `<tr>` gets id="<testId>-expanded-<rowKey>"
 */
@Component({
    selector: 'app-detail-table',
    standalone: true,
    imports: [
        CommonModule,
        UiDataTableComponent,
        UiTableCellDirective,
        UiTableExpandedDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './detail-table.component.html',
    styleUrls: ['./detail-table.component.scss']
})
export class DetailTableComponent<T> implements AfterContentInit {
    @Input({ required: true }) config!: DetailTableConfig<T>
    @Input({ required: true }) rows: T[] = []

    @Output() rowToggled = new EventEmitter<T>()

    @ContentChildren(DetailTableCellDirective)
    cellSlots!: QueryList<DetailTableCellDirective<T>>

    @ContentChild(DetailTableExpandedDirective)
    expandedSlot?: DetailTableExpandedDirective<T>

    /** column.id -> custom template. Built once in ngAfterContentInit so the
     *  template lookup is O(1) per cell. */
    private slotByColumn = new Map<string, DetailTableCellDirective<T>>()

    readonly uiRowKey = (row: T): string => this.rowKeyFor(row)
    readonly uiCanExpandRow = (row: T): boolean => this.canExpand(row)
    readonly uiRowClass = (row: T): string | string[] | null => this.summaryClassFor(row)
    readonly uiRowAttrs = (row: T): Record<string, string | null | undefined> => this.summaryAttrsFor(row)

    ngAfterContentInit(): void {
        this.rebuildSlotMap()
        this.cellSlots.changes.subscribe(() => this.rebuildSlotMap())
    }

    private rebuildSlotMap(): void {
        this.slotByColumn = new Map(this.cellSlots.map(s => [s.columnId, s]))
    }

    customCellTemplate(columnId: string): DetailTableCellDirective<T> | null {
        return this.slotByColumn.get(columnId) ?? null
    }

    /** Pull the raw value off a row using the column's accessor. */
    rawValue(row: T, column: DetailTableColumn<T>): unknown {
        const a = column.accessor
        if (a == null) return undefined
        if (typeof a === 'function') return a(row)
        return (row as any)[a]
    }

    /** Format the raw value using the column's `kind`. */
    formattedValue(row: T, column: DetailTableColumn<T>): string {
        const v = this.rawValue(row, column)
        if (v == null || v === '') return '—'
        switch (column.kind) {
            case 'minutes': {
                const n = typeof v === 'number' ? v : Number(v)
                return Number.isFinite(n) ? formatMinutes(n) : '—'
            }
            case 'percent': {
                const n = typeof v === 'number' ? v : Number(v)
                if (!Number.isFinite(n)) return '—'
                const pct = Math.round((n - 1) * 100)
                return (pct > 0 ? '+' : '') + pct + '%'
            }
            case 'integer': {
                const n = typeof v === 'number' ? v : Number(v)
                return Number.isFinite(n) ? n.toLocaleString() : '—'
            }
            case 'text':
            default:
                return String(v)
        }
    }

    get uiColumns(): UiDataTableColumn<T>[] {
        return this.config.columns.map(column => ({
            id: column.id,
            header: column.header,
            accessor: row => this.rawValue(row, column),
            hideable: false,
            visibleByDefault: true,
            align: column.align,
            headerClass: column.widthClass,
            cellClass: row => this.detailCellClass(column, row)
        }))
    }

    get expandedKeys(): ReadonlySet<string> {
        return this.config.expand?.expanded ?? new Set<string>()
    }

    isExpanded(row: T): boolean {
        const e = this.config.expand
        if (!e) return false
        return e.expanded.has(e.rowKey(row))
    }

    canExpand(row: T): boolean {
        const e = this.config.expand
        if (!e) return false
        return e.canExpand ? e.canExpand(row) : true
    }

    rowKeyFor(row: T): string {
        return this.config.expand ? this.config.expand.rowKey(row) : ''
    }

    expandedRowId(row: T): string {
        const test = this.config.testId ?? 'detail-table'
        return `${test}-expanded-${this.rowKeyFor(row)}`
    }

    expandedColspan(): number {
        const e = this.config.expand
        if (e?.colspan != null) return e.colspan
        // expander column + content columns
        return this.config.columns.length + (this.hasExpanderColumn ? 1 : 0)
    }

    get hasExpanderColumn(): boolean {
        return !!this.config.expand
    }

    onToggle(row: T): void {
        if (!this.canExpand(row)) return
        this.rowToggled.emit(row)
    }

    onExpanderClick(row: T, event?: Event): void {
        event?.stopPropagation()
        this.onToggle(row)
    }

    /** Default trackBy: row identity, or rowKey if expansion is configured. */
    trackByFn = (index: number, row: T): unknown => {
        if (this.config.trackBy) return this.config.trackBy(index, row)
        if (this.config.expand) return this.config.expand.rowKey(row)
        return row
    }

    /** Per-row class hook (consumer-supplied). */
    summaryClassFor(row: T): string | string[] | null {
        return this.config.summaryRowClass ? this.config.summaryRowClass(row) : null
    }

    /** Per-row data-* attributes hook (consumer-supplied). */
    summaryAttrsFor(row: T): Record<string, string | null | undefined> {
        return this.config.summaryRowAttrs ? this.config.summaryRowAttrs(row) : {}
    }

    get expanderTestId(): string {
        return this.config.expanderTestId
            ?? `${this.config.testId || 'detail-table'}-expander`
    }

    private detailCellClass(column: DetailTableColumn<T>, row: T): string[] {
        const classes: string[] = []
        if (column.widthClass) classes.push(column.widthClass)
        const custom = column.cellClass ? column.cellClass(row) : null
        if (Array.isArray(custom)) classes.push(...custom)
        else if (custom) classes.push(custom)
        return classes
    }
}
